import { Controller } from "@hotwired/stimulus"
import Uppy from "@uppy/core"
import AwsS3 from "@uppy/aws-s3"
import GoldenRetriever from "@uppy/golden-retriever"

// Resumable multipart upload straight to S3 quarantine. Custom multipart
// functions call our five endpoints; each part is signed with its exact
// Content-Length (D-005). Golden Retriever restores small files across
// reloads, but iOS Safari cannot re-materialize gigabyte file bytes — that
// leaves a "ghost" (restored metadata, no blob). We drop ghosts (see
// purgeGhosts) so the user's re-selection starts a clean upload rather than
// resuming against a stale, blob-less chunk plan.
export default class extends Controller {
  static targets = ["input", "submit", "bar", "barfill", "uploadingNote"]
  static values = { minBytes: Number, maxBytes: Number, partSize: { type: Number, default: 16 * 1024 * 1024 } }

  connect() {
    this.uppy = new Uppy({
      autoProceed: false,
      restrictions: { maxNumberOfFiles: 1, allowedFileTypes: [".zip", "application/zip"] }
    })
      .use(GoldenRetriever)
      .use(AwsS3, {
        shouldUseMultipart: true,
        // Chunk at our part size so Uppy's parts match what the server signs
        // and validates (D-005), and so the part count stays within the
        // server's max_parts (ceil(maxBytes / partSize)). Without this Uppy
        // uses its ~5 MB default, desyncing sizes and overflowing part count.
        getChunkSize: () => this.partSizeValue,
        createMultipartUpload: (file) => this.post("/uploads", {}),
        // Sign the exact byte size of the chunk Uppy is about to PUT. `body`
        // is the actual chunk Blob; its size is what uploadPartBytes sends, so
        // the signed Content-Length always matches the request body. A nullish
        // or empty body means there is no chunk data to upload (e.g. a part
        // past EOF); refuse rather than asking the server to sign a phantom,
        // non-positive-length part. Ghost files that would trigger this are
        // purged on restore (see purgeGhosts), so a real upload never hits it.
        signPart: (file, { uploadId, partNumber, body }) => {
          if (!(body?.size > 0)) {
            return Promise.reject(new Error(`refusing to sign part ${partNumber}: empty or missing body`))
          }
          return this.get("/uploads/sign_part", {
            upload_id: uploadId,
            part_number: partNumber,
            content_length: body.size
          })
        },
        listParts: (file, { uploadId }) =>
          this.get("/uploads/list_parts", { upload_id: uploadId }),
        completeMultipartUpload: (file, { uploadId, parts }) =>
          this.post("/uploads/complete", { upload_id: uploadId, parts }),
        abortMultipartUpload: (file, { uploadId }) =>
          this.post("/uploads/abort", { upload_id: uploadId })
      })

    // GoldenRetriever restores prior file state on connect, but a large export
    // whose blob can't be re-materialized comes back as a "ghost" (isGhost:
    // stale size, no data). This funnel has no recovery-confirmation UI to
    // reconcile ghosts, and an unreconciled ghost drives a stale chunk plan
    // that overshoots the real file's part count. Drop ghosts so the user's
    // re-selection always starts a clean upload.
    this.uppy.on("restored", () => this.purgeGhosts())

    this.uppy.on("upload-progress", (_file, progress) => this.renderProgress(progress))
    this.uppy.on("complete", () => { window.location.assign("/done") })

    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("change", (e) => this.addFiles(e.target.files))
    }
  }

  // Triggered by the "Submit my export" button (autoProceed is off so the user
  // reviews the file first, matching the prototype). Replace the button with
  // the progress bar + "Uploading…" note for the duration of the upload.
  start() {
    // Belt-and-suspenders: drop any ghost that outlived restore before we
    // build the chunk plan, independent of "restored" event timing.
    this.purgeGhosts()
    if (this.hasSubmitTarget) this.submitTarget.classList.add("hidden")
    if (this.hasBarTarget) this.barTarget.classList.remove("hidden")
    if (this.hasUploadingNoteTarget) this.uploadingNoteTarget.classList.remove("hidden")
    this.uppy.upload()
  }

  disconnect() {
    // Guard against Turbo Drive double-init / cached restores.
    if (this.uppy) this.uppy.destroy()
  }

  addFiles(fileList) {
    for (const file of fileList) {
      try {
        this.uppy.addFile({ name: file.name, type: file.type, data: file })
      } catch (err) {
        // Restriction failures surface via Uppy's own events.
      }
    }
  }

  // Remove GoldenRetriever "ghost" files (restored metadata with no recovered
  // blob). Their stale size would otherwise drive a chunk plan that overshoots
  // the real file, producing phantom parts. Dropping them forces a clean
  // re-selection instead of resuming against mismatched state.
  purgeGhosts() {
    this.uppy.getFiles().forEach((file) => {
      if (file.isGhost) this.uppy.removeFile(file.id)
    })
  }

  renderProgress(progress) {
    const pct = progress.bytesTotal ? Math.round((progress.bytesUploaded / progress.bytesTotal) * 100) : 0
    if (this.hasBarTarget) this.barTarget.classList.remove("hidden")
    if (this.hasBarfillTarget) this.barfillTarget.style.width = `${pct}%`
  }

  async post(path, body) {
    const res = await fetch(path, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(body)
    })
    return res.json()
  }

  async get(path, params) {
    const url = new URL(path, window.location.origin)
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v))
    const res = await fetch(url, { headers: this.headers() })
    return res.json()
  }

  headers() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return { "Content-Type": "application/json", "X-CSRF-Token": token || "" }
  }
}
